import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const INVOICE_API = "https://fetc.in/api/v1/invoices";

export const invoiceApi = createApi({
  reducerPath: "invoiceApi",
  baseQuery: fetchBaseQuery({
    baseUrl: INVOICE_API,
    credentials: "include",
    prepareHeaders: (headers) => {
      headers.set("Accept", "application/json");
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      } else {
        console.warn("No auth token found in localStorage.");
      }
      return headers;
    },
  }),
  tagTypes: ["Invoices"],
  endpoints: (builder) => ({
    getNextInvoiceNo: builder.query({
      query: () => ({
        url: "/next-invoice-no",
        method: "GET",
      }),
      transformResponse: (response) => ({
        success: true,
        invoiceNo: response.invoiceNo,
      }),
      transformErrorResponse: (response) => ({
        success: false,
        error: response?.data?.message || "Failed to fetch next invoice number",
        status: response?.status,
      }),
    }),
    createInvoice: builder.mutation({
      query: (formData) => ({
        url: "/",
        method: "POST",
        body: formData,
      }),
      transformResponse: (response) => ({
        success: true,
        data: response.data,
      }),
      transformErrorResponse: (response) => ({
        success: false,
        error: response?.data?.message || "Failed to create invoice",
        status: response?.status,
      }),
      invalidatesTags: [{ type: "Invoices" }],
    }),
    getInvoices: builder.query({
      query: ({ search, from, to } = {}) => ({
        url: "/list",
        method: "GET",
        params: { search, from, to },
      }),
      transformResponse: (response) => ({
        success: true,
        invoices: response.invoices || [],
        total: response.invoices?.length || 0,
      }),
      transformErrorResponse: (response) => ({
        success: false,
        error: response?.data?.message || "Failed to fetch invoices",
        status: response?.status,
      }),
      providesTags: (result) =>
        result?.invoices
          ? [
              ...result.invoices.map((inv) => ({
                type: "Invoices",
                id: inv.invoiceNo,
              })),
              { type: "Invoices" },
            ]
          : [{ type: "Invoices" }],
    }),
    getInvoiceByNo: builder.query({
      query: (invoiceNo) => ({
        url: `/${invoiceNo}`,
        method: "GET",
      }),
      transformResponse: (response) => ({
        success: true,
        data: response.data,
      }),
      transformErrorResponse: (response) => ({
        success: false,
        error: response?.data?.message || "Failed to fetch invoice",
        status: response?.status,
      }),
      providesTags: (result, error, invoiceNo) => [
        { type: "Invoices", id: invoiceNo },
      ],
    }),
   editInvoice: builder.mutation({
  query: ({ invoiceNo, body }) => ({
    url: `/${invoiceNo}`,
    method: "PUT",
    body,
  }),
      transformResponse: (response) => ({
        success: true,
        data: response.data,
      }),
      transformErrorResponse: (response) => ({
        success: false,
        error: response?.data?.message || "Failed to update invoice",
        status: response?.status,
      }),
      invalidatesTags: (result, error, { invoiceNo }) => [
        { type: "Invoices", id: invoiceNo },
        { type: "Invoices" },
      ],
    }),
    deleteInvoice: builder.mutation({
      query: (invoiceNo) => ({
        url: `/${invoiceNo}`,
        method: "DELETE",
      }),
      transformResponse: (response) => ({
        success: true,
        data: response,
      }),
      transformErrorResponse: (response) => ({
        success: false,
        error: response?.data?.message || "Failed to delete invoice",
        status: response?.status,
      }),
      invalidatesTags: (result, error, invoiceNo) => [
        { type: "Invoices", id: invoiceNo },
        { type: "Invoices" },
      ],
    }),
  }),
});

export const {
  useGetNextInvoiceNoQuery,
  useCreateInvoiceMutation,
  useGetInvoicesQuery,
  useGetInvoiceByNoQuery,
  useEditInvoiceMutation,
  useDeleteInvoiceMutation,
} = invoiceApi;